import React, { useCallback, useMemo, useRef, useState } from "react";
import Cropper, { Area, MediaSize } from "react-easy-crop";
import {
    Box,
    ButtonBase,
    Modal,
    Slider,
    Stack,
    Typography,
    useTheme,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "./Button";
import { addCloudinaryTransforms } from "../utils/cloudinaryUtils";

type Mode = "avatar" | "square" | "rectangle" | "wallpaper";

type Props = {
    open: boolean;
    /** Cloudinary URL of the ORIGINAL image (no transforms ideally) */
    src: string | null;
    onCancel: () => void;
    /** Returns a Cloudinary URL with crop + fill at the chosen size.
     * May be async — the modal awaits it and locks the Save button while it runs. */
    onSave: (finalUrl: string) => void | Promise<void>;
    onDelete?: () => void;

    /** Visual/crop behavior */
    mode?: Mode; // default: "avatar"
    /** Final output size for Cloudinary c_fill (w,h). If omitted, a sane default per mode is used. */
    outSize?: { w: number; h: number };
    /** Optional explicit aspect override (w/h). If not provided, derived from outSize. */
    aspect?: number;

    /** Wallpaper-only: visualize what's hidden under header/tab bars.
     * Provide absolute pixel heights relative to the FINAL output size (outSize.h).
     * Example: { top: 88, bottom: 84 } */
    wallpaperSafeArea?: { top: number; bottom: number };

    /** Zoom range; defaults to [1,3] */
    zoomRange?: { min: number; max: number };
    /** Optional custom title text */
    title?: string;
};

const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

const DEFAULTS: Record<
    Mode,
    { w: number; h: number; cropShape: "round" | "rect" }
> = {
    avatar: { w: 256, h: 256, cropShape: "round" },
    square: { w: 430, h: 430, cropShape: "rect" },
    rectangle: { w: 430, h: 300, cropShape: "rect" }, // arbitrary default; override via outSize
    wallpaper: { w: 430, h: 932, cropShape: "rect" }, // iPhone 14 Pro Max logical px; override via outSize (e.g., 1080x2336)
};

const ImageCropperModal: React.FC<Props> = ({
    open,
    src,
    onCancel,
    onSave,
    onDelete,
    mode = "avatar",
    outSize,
    aspect,
    wallpaperSafeArea,
    zoomRange,
    title,
}) => {
    const theme = useTheme();

    // Resolve effective sizing + crop visuals
    const spec = DEFAULTS[mode];
    const W = outSize?.w ?? spec.w;
    const H = outSize?.h ?? spec.h;
    const ASPECT = aspect ?? W / H;
    const cropShape = spec.cropShape;

    // Wallpaper mode: taller viewport + higher max zoom. minZoom stays at 1 so
    // the image always at least covers the crop frame — zooming out below 1
    // would expose "void" that can't be baked into a Cloudinary crop and that
    // the client (which renders the wallpaper `cover`) could never show.
    const effectiveZoomRange = zoomRange ?? (mode === "wallpaper" ? { min: 1, max: 5 } : { min: 1, max: 3 });
    const cropViewportHeight = mode === "wallpaper" ? "min(70vh, 560px)" : "360px";
    // Wallpaper renders `cover` on the client, so the cropper covers the frame
    // too (WYSIWYG). Other modes keep `contain` to show the whole source.
    const mediaObjectFit = mode === "wallpaper" ? "cover" : "contain";

    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation] = useState(0);
    // In-flight lock: prevents double-submit while onSave (network + Cloudinary rename) runs.
    const [saving, setSaving] = useState(false);
    const croppedAreaPixelsRef = useRef<Area | null>(null);
    // Natural pixel size of the loaded image — used to clamp the crop rect so
    // it can never spill past the image bounds (defence against void crops).
    const mediaSizeRef = useRef<MediaSize | null>(null);
    // Prevents mounting the Cropper with stale crop/zoom from the previous session.
    // Set to false on close, flipped to true after the reset effect has flushed.
    const [ready, setReady] = useState(false);

    const onCropComplete = useCallback((_cropped: Area, pixels: Area) => {
        croppedAreaPixelsRef.current = pixels;
    }, []);

    const onMediaLoaded = useCallback((mediaSize: MediaSize) => {
        mediaSizeRef.current = mediaSize;
    }, []);

    // Reset state when opening or source changes
    React.useEffect(() => {
        if (open) {
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            croppedAreaPixelsRef.current = null;
            mediaSizeRef.current = null;
            setSaving(false);
            setReady(true);
        } else {
            setReady(false);
        }
    }, [open, src]);

    const handleSave = async () => {
        if (saving || !src || !croppedAreaPixelsRef.current) return;

        const { x, y, width, height } = croppedAreaPixelsRef.current;

        // Clamp the crop rectangle to the image's real pixel bounds. When the
        // frame extends past the image (e.g. slight zoom-out into void), the
        // raw crop can have negative x/y or w/h beyond the image; baking those
        // into c_crop makes Cloudinary re-anchor to a region the user never
        // chose. Clamping keeps the crop a valid sub-rectangle that preserves
        // the chosen framing.
        const natW = mediaSizeRef.current?.naturalWidth ?? width;
        const natH = mediaSizeRef.current?.naturalHeight ?? height;
        const ix = Math.round(clamp(x, 0, Math.max(0, natW - 1)));
        const iy = Math.round(clamp(y, 0, Math.max(0, natH - 1)));
        const iw = Math.round(clamp(width, 1, natW - ix));
        const ih = Math.round(clamp(height, 1, natH - iy));

        // Crop → Fill to final WxH (keeps consistent output everywhere)
        const finalUrl = addCloudinaryTransforms(src, [
            "f_auto,q_auto",
            `c_crop,x_${ix},y_${iy},w_${iw},h_${ih}`,
            `c_fill,w_${W},h_${H}`,
        ]);

        setSaving(true);
        try {
            // onSave may be async (uploads + Cloudinary rename); await so the
            // button stays locked until it resolves, preventing double-submit.
            await Promise.resolve(onSave(finalUrl));
        } finally {
            setSaving(false);
        }
    };

    // Compute wallpaper overlay (top/bottom cut) as percentages of the crop viewport height
    const wallpaperOverlayPerc = useMemo(() => {
        if (mode !== "wallpaper" || !wallpaperSafeArea) return null;
        const { top, bottom } = wallpaperSafeArea;
        const topPct = clamp((top / H) * 100, 0, 100);
        const bottomPct = clamp((bottom / H) * 100, 0, 100);
        // Ensure total doesn’t exceed 100 so we don’t overlap weirdly
        return {
            topPct,
            bottomPct,
            show: topPct + bottomPct < 100,
        };
    }, [mode, wallpaperSafeArea, H]);

    return (
        <Modal open={open} onClose={saving ? undefined : onCancel}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    bgcolor: "#fff",
                    width: "min(92vw, 480px)",
                    borderRadius: "16px",
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <Stack
                    className="force-dynamic-translation"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    px={2}
                    py={1.5}
                >
                    <Typography fontWeight={700}>
                        {title ??
                            (mode === "avatar"
                                ? "Adjust your photo"
                                : mode === "wallpaper"
                                ? "Adjust your wallpaper"
                                : "Adjust your image")}
                    </Typography>
                    <IconButton
                        onClick={onCancel}
                        size="small"
                        disabled={saving}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>

                {/* Crop area */}
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: cropViewportHeight,
                        bgcolor: "#111",
                        overflow: "hidden",
                    }}
                >
                    {src && ready && (
                        <>
                            <Cropper
                                image={src}
                                crop={crop}
                                zoom={zoom}
                                minZoom={effectiveZoomRange.min}
                                maxZoom={effectiveZoomRange.max}
                                rotation={rotation}
                                aspect={ASPECT}
                                cropShape={cropShape}
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                                onMediaLoaded={onMediaLoaded}
                                objectFit={mediaObjectFit}
                            />

                            {/* Wallpaper safe area overlay (non-interactive) */}
                            {mode === "wallpaper" &&
                                wallpaperOverlayPerc?.show && (
                                    <>
                                        {/* Top bar — area hidden under header */}
                                        {wallpaperOverlayPerc.topPct > 0 && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: `${wallpaperOverlayPerc.topPct}%`,
                                                    background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.08) 100%)",
                                                    pointerEvents: "none",
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    justifyContent: "center",
                                                    pt: "4px",
                                                }}
                                            >
                                                <Typography
                                                    fontSize="9px"
                                                    fontWeight={600}
                                                    color="rgba(255,255,255,0.7)"
                                                    textTransform="uppercase"
                                                    letterSpacing="0.5px"
                                                >
                                                    Header overlay
                                                </Typography>
                                            </Box>
                                        )}
                                        {/* Bottom bar — glass tab bar overlay zone */}
                                        {wallpaperOverlayPerc.bottomPct > 0 && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: `${wallpaperOverlayPerc.bottomPct}%`,
                                                    background: "linear-gradient(0deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.08) 100%)",
                                                    pointerEvents: "none",
                                                    display: "flex",
                                                    alignItems: "flex-end",
                                                    justifyContent: "center",
                                                    pb: "4px",
                                                }}
                                            >
                                                <Typography
                                                    fontSize="9px"
                                                    fontWeight={600}
                                                    color="rgba(255,255,255,0.7)"
                                                    textTransform="uppercase"
                                                    letterSpacing="0.5px"
                                                >
                                                    Tab bar overlay
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                )}
                        </>
                    )}
                </Box>

                {/* Controls */}
                <Stack
                    className="force-dynamic-translation"
                    spacing={2}
                    px={3}
                    py={2}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            id="zoom-slider-label"
                        >
                            Zoom
                        </Typography>
                        <ButtonBase
                            onClick={() => {
                                setCrop({ x: 0, y: 0 });
                                setZoom(1);
                            }}
                            sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                px: 1,
                                py: 0.25,
                                borderRadius: "6px",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                            }}
                        >
                            Reset
                        </ButtonBase>
                    </Stack>
                    <Slider
                        value={zoom}
                        min={effectiveZoomRange.min}
                        max={effectiveZoomRange.max}
                        step={0.01}
                        onChange={(_, v) => setZoom(v as number)}
                        aria-labelledby="zoom-slider-label"
                    />

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        mt={1}
                    >
                        {onDelete && (
                            <Button
                                variant="medium"
                                color="transparent"
                                customColor={theme.palette.error.main}
                                disabled={saving}
                                onClick={() => {
                                    onDelete?.();
                                    onCancel();
                                }}
                            >
                                Delete
                            </Button>
                        )}
                        <Box sx={{ flex: 1 }} />{" "}
                        {/* keep Save anchored to the right */}
                        <Button
                            variant="medium"
                            color="primary"
                            onClick={handleSave}
                            disabled={!src || saving}
                            loading={saving}
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </Modal>
    );
};

export default ImageCropperModal;
